'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/navigation'
import { completeLessonPart } from '@/app/lesson/actions'
import { submitTopicResult } from '@/app/grammar/actions'
import { guestSummary, hasGuestProgress, importGuestProgress, readGuestProgress, type ImportSenders } from '@/lib/guest-progress'
import { reachGoal } from '@/lib/analytics'
import { lessonsWord } from '@/components/guest-sign-in'

// Отправка шага переноса на сервер с пометкой "гостевой": опыт и акче в профиль,
// без лиги и серии (миграция 20261017200000_guest_import.sql); тренажёр правила
// без опыта (recordActivity = false). Время урока уходит с последней частью,
// чтобы не задвоилось.
export const guestSenders: ImportSenders = {
  part: (l, part) =>
    completeLessonPart(
      l.lessonId,
      part,
      l.vocabIds,
      l.missedIds,
      l.timezone,
      part === Math.min(3, l.parts) ? l.seconds : 0,
      part === 3 ? l.score : null,
      null,
      true,
    ),
  topic: (t) => submitTopicResult(t.topicId, t.correct, t.total, t.mistakes, t.timezone, t.seconds, 'lesson', false),
}

// Один перенос за раз: страница /guest-sync и LessonSyncFlusher (вход не через
// кнопки гостя) могут запуститься одновременно.
let inFlight: Promise<{ sent: number; failed: boolean }> | null = null
export function runGuestImport(): Promise<{ sent: number; failed: boolean }> {
  if (!inFlight) inFlight = importGuestProgress(guestSenders).finally(() => (inFlight = null))
  return inFlight
}

export function GuestSync({ next }: { next: string }) {
  const router = useRouter()
  const [state, setState] = useState<'saving' | 'failed'>('saving')
  const [lessons, setLessons] = useState(0)
  const started = useRef(false)

  const run = useCallback(async () => {
    if (!hasGuestProgress()) {
      router.replace(next)
      return
    }
    const before = guestSummary(readGuestProgress())
    setState('saving')
    setLessons(before.lessons)
    const r = await runGuestImport()
    if (r.failed && hasGuestProgress()) {
      setState('failed')
      return
    }
    reachGoal('guest_import_done', { lessons: before.lessons })
    router.replace(next)
    router.refresh()
  }, [next, router])

  useEffect(() => {
    if (started.current) return
    started.current = true
    void run()
  }, [run])

  return (
    <div className="tk-step-shell mx-auto flex min-h-svh w-full max-w-[560px] flex-col items-center justify-center gap-4 px-5 text-center">
      {state === 'saving' ? (
        <div className="text-[16px] text-fg-muted">{lessons > 0 ? `Сохраняем ${lessons} ${lessonsWord(lessons)} в аккаунт...` : 'Открываем урок...'}</div>
      ) : (
        <>
          <div className="text-[16px]">Не получилось сохранить уроки: нет связи с сервером. Они остались в этом браузере.</div>
          <button type="button" className="tk-btn-primary inline-flex w-full items-center justify-center rounded-full px-8 py-4 text-[16px] font-bold" onClick={() => void run()}>
            Попробовать ещё раз
          </button>
          <button type="button" className="tk-pill tk-pill--sm" onClick={() => router.replace(next)}>
            Продолжить, сохраним позже
          </button>
        </>
      )}
    </div>
  )
}
